import { useEffect, useRef, useState } from "react";
import { Heart, FlaskConical, Pill, FileText, Pencil, Plus, Download, Trash2, X, RefreshCw } from "@/components/icons";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatErr } from "../../../lib/api";
import { Card, SectionHeader, Empty, fmtDate, fmtTime } from "./common";
import Portal from "../../../components/Portal";

// -----------------------------------------------------------------------------
// Reusable Table primitives — minimalist tokens (matches Finanzas / Prescriptions)
// -----------------------------------------------------------------------------
function ColHeader({ children }) {
  return (
    <th className="pb-2 px-3 first:pl-0 last:pr-0 text-[10px] uppercase tracking-widest text-black/40 font-semibold text-left">
      {children}
    </th>
  );
}

function Row({ children }) {
  return (
    <tr className="border-t border-black/[0.05] text-black hover:bg-black/[0.015] transition">
      {children}
    </tr>
  );
}

function Cell({ children, bold = false, className = "" }) {
  return (
    <td
      className={`py-3 px-3 first:pl-0 last:pr-0 align-top text-[12.5px] ${
        bold ? "font-semibold text-black" : "text-black/70"
      } ${className}`}
    >
      {children}
    </td>
  );
}

function DataTable({ headers, children, testId }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" data-testid={testId}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <ColHeader key={h + i}>{h}</ColHeader>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Tabs
// -----------------------------------------------------------------------------
export function VitalsTab({ diagnoses, isDoctor, onAdd, onEdit }) {
  const withVitals = diagnoses
    .filter((d) => d.vitals && Object.values(d.vitals).some(Boolean))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return (
    <Card>
      <SectionHeader
        icon={Heart}
        title="Historial de signos vitales"
        onAdd={isDoctor ? onAdd : null}
      />
      {withVitals.length === 0 ? (
        <Empty>Sin registros de vitales</Empty>
      ) : (
        <DataTable
          headers={["Fecha", "PA", "FC", "T°", "FR", "SpO₂", "Peso", "Altura", ""]}
          testId="vitals-table"
        >
          {withVitals.map((d) => (
            <Row key={d.id}>
              <Cell bold>{fmtDate(d.created_at)}</Cell>
              <Cell>{d.vitals.blood_pressure || "—"}</Cell>
              <Cell>{d.vitals.heart_rate || "—"}</Cell>
              <Cell>{d.vitals.temperature || "—"}</Cell>
              <Cell>{d.vitals.respiration_rate || "—"}</Cell>
              <Cell>{d.vitals.oxygen_saturation || "—"}</Cell>
              <Cell>{d.vitals.weight || "—"}</Cell>
              <Cell>{d.vitals.height || "—"}</Cell>
              <Cell className="text-right whitespace-nowrap">
                {isDoctor && onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(d)}
                    data-testid={`vitals-edit-${d.id}`}
                    title="Editar vitales"
                    className="text-black/60 hover:text-black inline-flex items-center gap-1 text-[12px] font-semibold transition"
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" /> Editar
                  </button>
                )}
              </Cell>
            </Row>
          ))}
        </DataTable>
      )}
    </Card>
  );
}

export function LabTab({ patientId, isDoctor }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null); // { fileBase64, name, mime }
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    if (!patientId) return;
    let cancelled = false;
    api
      .get(`/lab-results?patient_id=${patientId}`)
      .then((r) => {
        if (!cancelled) {
          setItems(r.data || []);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [patientId, reloadKey]);

  const reload = () => setReloadKey((k) => k + 1);

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este resultado de laboratorio?")) return;
    try {
      await api.delete(`/lab-results/${id}`);
      toast.success("Eliminado");
      reload();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  return (
    <Card>
      <SectionHeader
        icon={FlaskConical}
        title="Laboratorio y exploración"
        onAdd={isDoctor ? () => setCreating(true) : null}
      />
      {loading ? (
        <Empty>Cargando…</Empty>
      ) : items.length === 0 ? (
        <Empty>Sin resultados de laboratorio</Empty>
      ) : (
        <div className="space-y-3" data-testid="lab-list">
          {items.map((r) => (
            <div
              key={r.id}
              className="border-t border-black/[0.05] first:border-t-0 pt-3 first:pt-0"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-black/45">
                    {fmtDate(r.created_at)} · {fmtTime(r.created_at)}
                  </div>
                  <div className="text-[14px] font-semibold text-black mt-0.5">
                    {r.title}
                  </div>
                  {r.notes && (
                    <p className="text-[12px] text-black/70 mt-1.5 whitespace-pre-line">
                      {r.notes}
                    </p>
                  )}
                </div>
                {isDoctor && (
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => setEditing(r)}
                      data-testid={`lab-edit-${r.id}`}
                      title="Editar"
                      className="w-7 h-7 rounded-md hover:bg-black/[0.05] text-black/60 hover:text-black flex items-center justify-center transition"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                    </button>
                    <button
                      type="button"
                      onClick={() => del(r.id)}
                      data-testid={`lab-delete-${r.id}`}
                      title="Eliminar"
                      className="w-7 h-7 rounded-md hover:bg-red-50 text-red-500/70 hover:text-red-600 flex items-center justify-center transition"
                    >
                      <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" />
                    </button>
                  </div>
                )}
              </div>
              {(r.files || []).length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3">
                  {r.files.map((f, i) => (
                    <button
                      type="button"
                      key={i}
                      onClick={() => setViewing(f)}
                      data-testid={`lab-file-${r.id}-${i}`}
                      className="bg-white rounded-lg p-2 text-left hover:bg-black/[0.02] transition group border border-black/[0.08]"
                    >
                      <div className="aspect-video bg-[#F2F3F5] rounded-md overflow-hidden flex items-center justify-center mb-1.5">
                        {f.mime?.startsWith("image/") ? (
                          <img
                            src={f.data_url}
                            alt={f.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <FileText className="w-6 h-6 text-black/40" strokeWidth={1.75} weight="regular" />
                        )}
                      </div>
                      <div className="text-[11px] font-semibold text-black truncate">
                        {f.name}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {creating && (
        <LabResultModal
          patientId={patientId}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            reload();
          }}
        />
      )}
      {editing && (
        <LabResultModal
          patientId={patientId}
          existing={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
      {viewing && <LabFileViewer file={viewing} onClose={() => setViewing(null)} />}
    </Card>
  );
}

function LabResultModal({ patientId, existing = null, onClose, onSaved }) {
  const isEditing = Boolean(existing?.id);
  const [title, setTitle] = useState(existing?.title || "");
  const [notes, setNotes] = useState(existing?.notes || "");
  const [files, setFiles] = useState(existing?.files || []);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const onPick = (e) => {
    const list = Array.from(e.target.files || []);
    list.forEach((f) => {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name}: máx 5MB`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setFiles((prev) => [
          ...prev,
          { name: f.name, mime: f.type, data_url: reader.result },
        ]);
      };
      reader.readAsDataURL(f);
    });
    e.target.value = "";
  };

  const save = async (e) => {
    e.preventDefault();
    if (!title.trim()) {
      toast.error("Indica un título");
      return;
    }
    setSaving(true);
    try {
      if (isEditing) {
        await api.put(`/lab-results/${existing.id}`, {
          title: title.trim(),
          notes: notes.trim() || null,
          files,
        });
        toast.success("Resultado actualizado");
      } else {
        await api.post("/lab-results", {
          patient_id: patientId,
          title: title.trim(),
          notes: notes.trim() || null,
          files,
        });
        toast.success("Resultado guardado");
      }
      onSaved();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 fade-up"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <form
          onSubmit={save}
          onMouseDown={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl border border-black/[0.05] shadow-2xl w-full max-w-lg max-h-[92vh] overflow-hidden flex flex-col"
        >
          <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-black/[0.05]">
            <h3 className="text-[15px] font-semibold text-black">
              {isEditing ? "Editar resultado de laboratorio" : "Nuevo resultado de laboratorio"}
            </h3>
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 rounded-lg hover:bg-black/[0.05] flex items-center justify-center text-black/60 transition"
            >
              <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
            </button>
          </div>

          <div className="p-5 space-y-4 overflow-y-auto no-scrollbar flex-1">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
                Título
              </label>
              <input
                data-testid="lab-title"
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej. Hemograma completo"
                className="w-full h-10 rounded-lg border border-black/[0.08] bg-white px-3 text-[13px] text-black focus:outline-none focus:border-black/40 transition"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
                Notas
              </label>
              <textarea
                data-testid="lab-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
                placeholder="Observaciones, valores, recomendaciones..."
                className="w-full rounded-lg border border-black/[0.08] bg-white px-3 py-2 text-[13px] text-black resize-none focus:outline-none focus:border-black/40 transition"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-widest font-semibold text-black/45 block mb-1">
                Archivos (imágenes o PDF, máx 5MB c/u)
              </label>
              <div className="flex flex-wrap gap-1.5 items-center">
                {files.map((f, i) => (
                  <div
                    key={i}
                    className="bg-[#F2F3F5] rounded-md px-2.5 py-1 text-[11px] font-semibold text-black flex items-center gap-1.5"
                  >
                    {f.name}
                    <button
                      type="button"
                      onClick={() =>
                        setFiles((prev) => prev.filter((_, j) => j !== i))
                      }
                      className="text-black/45 hover:text-red-600 transition"
                    >
                      <X className="w-3 h-3" strokeWidth={1.75} weight="regular" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  data-testid="lab-pick-files"
                  className="bg-[#F2F3F5] rounded-md px-2.5 py-1 text-[11px] font-semibold text-black hover:bg-black/[0.08] inline-flex items-center gap-1 transition"
                >
                  <Plus className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" /> Añadir
                </button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={onPick}
                  className="hidden"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-2 px-5 py-3 border-t border-black/[0.05] bg-white">
            <button
              type="button"
              onClick={onClose}
              className="h-10 px-4 rounded-lg bg-white border border-black/10 hover:bg-black/[0.04] text-[13px] font-semibold text-black transition"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="lab-save"
              className="h-10 px-5 rounded-lg bg-black text-white hover:bg-black/85 text-[13px] font-semibold transition disabled:opacity-60"
            >
              {saving ? "Guardando…" : isEditing ? "Guardar cambios" : "Guardar"}
            </button>
          </div>
        </form>
      </div>
    </Portal>
  );
}

function LabFileViewer({ file, onClose }) {
  const isImage = file.mime?.startsWith("image/");
  return (
    <Portal>
      <div
        className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 fade-up"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose?.();
        }}
      >
        <div
          onMouseDown={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl border border-black/[0.05] shadow-2xl p-4 max-w-3xl w-full max-h-[90vh] overflow-auto"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="text-[13px] font-semibold text-black truncate">{file.name}</div>
            <div className="flex items-center gap-1">
              <a
                href={file.data_url}
                download={file.name}
                className="w-8 h-8 rounded-lg hover:bg-black/[0.05] text-black/70 hover:text-black flex items-center justify-center transition"
                title="Descargar"
              >
                <Download className="w-4 h-4" strokeWidth={1.75} weight="regular" />
              </a>
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 rounded-lg hover:bg-black/[0.05] text-black/60 flex items-center justify-center transition"
              >
                <X className="w-4 h-4" strokeWidth={1.75} weight="regular" />
              </button>
            </div>
          </div>
          {isImage ? (
            <img src={file.data_url} alt={file.name} className="w-full rounded-lg" />
          ) : (
            <iframe
              src={file.data_url}
              title={file.name}
              className="w-full h-[70vh] rounded-lg bg-[#F2F3F5]"
            />
          )}
        </div>
      </div>
    </Portal>
  );
}

export function MedicationTab({ patientId, prescriptions, isDoctor, onAdd, onEdit, onReload }) {
  const sorted = [...prescriptions].sort(
    (a, b) => new Date(b.created_at) - new Date(a.created_at)
  );

  const redo = async (rx) => {
    if (!window.confirm("¿Crear una nueva receta con los mismos medicamentos y la fecha de hoy?")) return;
    try {
      await api.post("/prescriptions", {
        patient_id: rx.patient_id || patientId,
        appointment_id: null,
        diagnosis: rx.diagnosis || "",
        notes: rx.notes || "",
        items: (rx.items || []).map(({ medication, dosage, frequency, duration }) => ({
          medication,
          dosage,
          frequency,
          duration,
        })),
        pharmacy_ids: rx.pharmacy_ids || [],
      });
      toast.success("Nueva receta creada con la fecha de hoy");
      onReload?.();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  return (
    <Card>
      <SectionHeader
        icon={Pill}
        title="Medicación e historial de recetas"
        onAdd={isDoctor ? onAdd : null}
      />
      {sorted.length === 0 ? (
        <Empty>Sin recetas</Empty>
      ) : (
        <DataTable
          headers={["Fecha", "Diagnóstico", "Medicamentos", ""]}
          testId="medication-table"
        >
          {sorted.map((rx) => (
            <Row key={rx.id}>
              <Cell bold className="whitespace-nowrap">
                {fmtDate(rx.created_at)}
              </Cell>
              <Cell bold>{rx.diagnosis || "Receta"}</Cell>
              <Cell>
                <ul className="space-y-1">
                  {(rx.items || []).map((m, i) => (
                    <li key={i}>
                      <span className="font-bold text-black">{m.medication}</span>{" "}
                      <span className="text-black/65">
                        · {m.dosage} · {m.frequency}
                        {m.duration ? ` · ${m.duration}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              </Cell>
              <Cell className="text-right whitespace-nowrap">
                <div className="inline-flex items-center gap-3">
                  {isDoctor && onEdit && (
                    <button
                      type="button"
                      onClick={() => onEdit(rx)}
                      data-testid={`rx-edit-${rx.id}`}
                      title="Editar receta"
                      className="text-black/60 hover:text-black inline-flex items-center gap-1 text-[12px] font-semibold transition"
                    >
                      <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" /> Editar
                    </button>
                  )}
                  {isDoctor && (
                    <button
                      type="button"
                      onClick={() => redo(rx)}
                      data-testid={`rx-redo-${rx.id}`}
                      title="Rehacer con fecha de hoy"
                      className="text-black/60 hover:text-black inline-flex items-center gap-1 text-[12px] font-semibold transition"
                    >
                      <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" /> Rehacer
                    </button>
                  )}
                  <Link
                    to={`/recetas/${rx.id}/imprimir`}
                    target="_blank"
                    className="text-[12px] font-semibold text-black hover:underline"
                  >
                    Ver / Imprimir →
                  </Link>
                </div>
              </Cell>
            </Row>
          ))}
        </DataTable>
      )}
    </Card>
  );
}

export function NotesTab({ diagnoses, isDoctor, onAdd, onEdit }) {
  const sorted = diagnoses
    .filter((d) => d.notes || d.symptoms)
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  return (
    <Card>
      <SectionHeader
        icon={FileText}
        title="Notas y Historial Clínicos"
        onAdd={isDoctor ? onAdd : null}
      />
      {sorted.length === 0 ? (
        <Empty>Sin notas registradas</Empty>
      ) : (
        <DataTable
          headers={["Fecha", "Título", "Síntomas", "Notas", ""]}
          testId="notes-table"
        >
          {sorted.map((d) => (
            <Row key={d.id}>
              <Cell bold className="whitespace-nowrap">
                {fmtDate(d.created_at)}
              </Cell>
              <Cell bold>{d.diagnosis || "Nota"}</Cell>
              <Cell className="max-w-[260px]">{d.symptoms || "—"}</Cell>
              <Cell className="max-w-[420px]">{d.notes || "—"}</Cell>
              <Cell className="text-right whitespace-nowrap">
                {isDoctor && onEdit && (
                  <button
                    type="button"
                    onClick={() => onEdit(d)}
                    data-testid={`note-edit-${d.id}`}
                    title="Editar nota"
                    className="text-black/60 hover:text-black inline-flex items-center gap-1 text-[12px] font-semibold transition"
                  >
                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.75} weight="regular" /> Editar
                  </button>
                )}
              </Cell>
            </Row>
          ))}
        </DataTable>
      )}
    </Card>
  );
}
